import { SparklesIcon } from "@heroicons/react/24/outline";
import { normalizeTime } from "@utils/utils";
import React, { useCallback, useEffect } from "react";

import { Headline, Small, Tooltip, WYSIWYG } from "@fronterahealth/frontera-ui-components";

import { NoData } from "@pages/VideoSessionsV2/components/NoData/NoData";
import { DataLoading } from "@pages/VideoSessionsV2/components/Utils/DataLoading";
import { convertTimestampToSeconds } from "@pages/VideoSessionsV2/components/Utils/helper";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface VideoSummaryProps {
  onTimeStampClick: (time: number) => void;
  onDurationClick: (startTime: number, endTime: number) => void;
}

declare global {
  interface Window {
    handleTimestampClick: (start: string, end?: string) => void;
  }
}

const convertTimestampsToButtons = () => {
  const container = document.getElementById("markdownSummary");
  if (!container) return;

  const timestampRegex = /\b(\d{1,2}:\d{2}(?::\d{2})?)(?:-(\d{1,2}:\d{2}(?::\d{2})?))?\b/g;

  // Skip if a button with data-processed="true" already exists
  if (container.querySelector('[data-processed="true"]')) {
    return;
  }

  container.innerHTML = container.innerHTML.replace(timestampRegex, (_, start, end) => {
    const normalizeStartTime = normalizeTime(start);
    const normalizeEndTime = end ? normalizeTime(end) : "";

    if (end) {
      return `<button data-start="${normalizeStartTime}" data-end="${normalizeEndTime}" data-processed="true" class="text-blue-500 mx-1 cursor-pointer">${start}-${end}</button>`;
    } else {
      return `<button data-start="${normalizeStartTime}" data-end="" data-processed="true" class="text-blue-500 mx-1 cursor-pointer">${start}</button>`;
    }
  });
};

export const VideoSummary: React.FC<VideoSummaryProps> = ({ onDurationClick, onTimeStampClick }) => {
  const { getSessionSummaryBySessionIdQuery } = useVideoSessionData();
  const { data, isFetching } = getSessionSummaryBySessionIdQuery;
  const sessionSummary = data?.getSessionSummaryBySessionId?.prediction;
  const trimmedSummary = sessionSummary ? sessionSummary?.replace(/Session Description:\s*/i, "").trim() : "";

  useEffect(() => {
    if (data?.getSessionSummaryBySessionId) {
      convertTimestampsToButtons();
    }
  }, [data]);

  // Callback function to handle timestamp clicks
  const handleTimestampClick = useCallback(
    (start: string, end?: string) => {
      const startTime = convertTimestampToSeconds(start);
      const endTime = end ? convertTimestampToSeconds(end) : undefined;

      if (endTime !== undefined) {
        onDurationClick(startTime, endTime);
      } else {
        onTimeStampClick(startTime);
      }
    },
    [onDurationClick, onTimeStampClick],
  );

  useEffect(() => {
    if (data?.getSessionSummaryBySessionId && !isFetching) {
      const container = document.getElementById("markdownSummary");
      if (!container) return;

      container.querySelectorAll("button").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const target = e.currentTarget as HTMLButtonElement;
          const start = target.getAttribute("data-start") || "0";
          const end = target.getAttribute("data-end") || undefined;

          handleTimestampClick(start, end);
        });
      });
    }
  }, [data]);

  return (
    <div className="bg-white rounded-lg p-4 pt-4 gap-3 grid grid-flow-row auto-rows-max cursor-pointer max-lg:min-h-[calc(100vh-18rem)] lg:overflow-y-auto lg:h-[calc(100vh-10rem)] ">
      {data?.getSessionSummaryBySessionId ? (
        <div className="flex flex-row justify-between">
          <Headline size="medium" className="text-text-primary">
            Session Summary{" "}
          </Headline>
          <div className="flex flex-row gap-1 items-center">
            <div className="flex items-center space-x-1  text-blurple-500 ml-4">
              <SparklesIcon className="h-4 w-4" />
              <Small className="text-blurple-500">Experimental</Small>
            </div>{" "}
            <Tooltip
              showInfoIcon
              text="Our AI models are still learning to interpret therapy sessions. Summaries may miss key moments or highlight less relevant ones. Use them as a helpful guide, but always review videos carefully yourself."
              className="bg-white border border-limestone-100 shadow-md px-4 py-2 rounded-lg"
              tooltipTextClassName="text-text-primary!"
              infoIconClassName="w-4! h-4!"
            />
          </div>
        </div>
      ) : null}

      {(() => {
        if (isFetching) {
          return (
            <div className="flex w-full flex-row justify-center">
              <DataLoading text="Session Summary loading.." />
            </div>
          );
        }
        if (data?.getSessionSummaryBySessionId) {
          return (
            <div id="markdownSummary" className="ph-no-capture">
              <WYSIWYG
                displayOnlyMode
                title=""
                onSave={async () => {}}
                isSaving={false}
                placeholder=""
                existingContent={trimmedSummary}
                aiGenerated={true}
              />
            </div>
          );
        }
        return (
          <NoData
            title="Summary Unavailable for This Session"
            description="We couldn’t generate a session summary for this video. This might happen due to certain system limitations or the nature of the session content. Please review the video directly for any insights or observations you need."
          />
        );
      })()}
    </div>
  );
};
